import React, { useState, useMemo, useRef, useEffect } from "react";
import { X } from "lucide-react";
import InputBox from "./InputBox";

interface Option {
  _id: string;
  CategoryName: string;
}

interface MultiSelectProps {
  value: string[];
  title: string;
  name: string;
  options: Option[];
  onChange: (selected: string[]) => void;
  mandatory?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ value, title, name, options, onChange, mandatory = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Auto close when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(
    () =>
      options
        .filter((opt) => opt?.CategoryName) // remove invalid items
        .filter((opt) =>
          opt.CategoryName.toLowerCase().includes(search.toLowerCase())
        ),
    [options, search]
  );
  const toggleSelect = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div className='w-full' ref={dropdownRef}>
      <div className='flex'>
        <p className='text-sm font-medium'>{title} </p>
        {mandatory && <p className='text-red-600'>*</p>}
      </div>

      <div className='relative'>
        {/* Button */}
        <button
          type='button'
          onClick={() => setShowDropdown((prev) => !prev)}
          className='w-full px-3 py-2 border rounded-md text-left bg-white hover:bg-gray-50 flex items-center justify-between'
        >
          <span className='text-gray-600'>{value.length > 0 ? `${value.length} selected` : `Select ${title}`}</span>
          <svg className='size-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </button>

        {/* Dropdown */}
        {showDropdown && (
          <div className='absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto p-2'>
            {/* Search */}
            <InputBox placeholder='Search...' value={search} onChange={(e) => setSearch(e.target.value)} className='mb-2' />

            {/* Items */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <label key={opt._id} className='flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer'>
                  <input type='checkbox' checked={value.includes(opt._id)} onChange={() => toggleSelect(opt._id)} />
                  <span>{opt.CategoryName}</span>
                </label>
              ))
            ) : (
              <p className='text-center text-gray-400 py-2 text-sm'>No results found</p>
            )}
          </div>
        )}
      </div>

      {/* Selected Badges */}
      {value.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {value.map((id) => {
            const item = options.find((o) => o._id === id);
            return (
              <span key={id} className='flex items-center gap-1 bg-gray-200 px-2 py-1 rounded text-sm'>
                {item?.CategoryName}
                <button onClick={() => toggleSelect(id)} className='hover:text-red-600'>
                  <X className='size-3' />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
